import { getHeroDef } from './heroes.ts';
import { itemBonuses } from './items.ts';
import type { AbilitySlot, HeroId } from './types.ts';

/**
 * Progresión estilo LoL: niveles 1-18 por experiencia, un punto de habilidad
 * por nivel. Q/W/E hasta rango 5 (nunca por encima de ceil(nivel/2)); la R
 * hasta rango 3, desbloqueable en los niveles 6, 11 y 16.
 */
export const MAX_LEVEL = 18;

export const XP = {
  /** por minion enemigo muerto, repartida entre los héroes cercanos */
  minion: 90,
  /** distancia a la que un héroe recibe experiencia de un minion */
  range: 14,
  /** por matar a un héroe: base + por nivel de la víctima */
  heroKillBase: 200,
  heroKillPerLevel: 40,
};

/** Experiencia necesaria para pasar de `level` a `level + 1`. */
export function xpToNext(level: number): number {
  return 180 + 80 * (level - 1);
}

export const RANKS = {
  basicMax: 5,
  ultimateMax: 3,
  ultimateLevels: [6, 11, 16],
};

/** Rango máximo permitido de una habilidad a cierto nivel. */
export function maxRank(slot: AbilitySlot, level: number): number {
  if (slot === 'r') return RANKS.ultimateLevels.filter((l) => level >= l).length;
  return Math.min(RANKS.basicMax, Math.ceil(level / 2));
}

export function canRankUp(hero: { level: number; skillPoints: number; ranks: Record<AbilitySlot, number> }, slot: AbilitySlot): boolean {
  return hero.skillPoints > 0 && hero.ranks[slot] < maxRank(slot, hero.level);
}

/** Multiplicador de daño de una habilidad según su rango (rango 1 = base). */
export function abilityDamageScale(slot: AbilitySlot, rank: number): number {
  const r = Math.max(1, rank);
  return slot === 'r' ? 1 + 0.35 * (r - 1) : 1 + 0.15 * (r - 1);
}

/** Multiplicador de cooldown según el rango (más rango, menos espera). */
export function abilityCooldownScale(slot: AbilitySlot, rank: number): number {
  const r = Math.max(1, rank);
  return slot === 'r' ? 1 - 0.15 * (r - 1) : 1 - 0.07 * (r - 1);
}

export interface HeroStats {
  maxHp: number;
  /** regeneración de vida por segundo (fuera de combate) */
  hpRegen: number;
  /** daño de ataque (AD) */
  attackDamage: number;
  /** multiplicador de velocidad de ataque (1 = base) */
  attackSpeed: number;
  /** multiplicador del alcance del autoataque (1 = base) */
  attackRange: number;
  armor: number;
  /** aceleración de habilidad: reduce los cooldowns (0 = base) */
  abilityHaste: number;
  /** velocidad de movimiento */
  moveSpeed: number;
  /** fracción del daño infligido que se cura (omnisucción) */
  omnivamp: number;
  /** letalidad: penetración plana de armadura */
  lethality: number;
  /** fracción (0..1) de penetración de armadura */
  armorPen: number;
  /** fracción (0..1) de reducción de la duración de control */
  tenacity: number;
}

/** Estadísticas completas de un héroe a cierto nivel, con los objetos de su inventario. */
export function heroStats(heroId: HeroId, level: number, items: readonly string[] = []): HeroStats {
  const def = getHeroDef(heroId);
  const growth = Math.max(0, level - 1);
  const bonus = itemBonuses(items);
  return {
    maxHp: Math.round(def.maxHp + def.hpPerLevel * growth + bonus.maxHp),
    hpRegen: def.hpRegen + bonus.hpRegen,
    attackDamage: def.attacks[0].damage + def.attackDamagePerLevel * growth + bonus.attackDamage,
    // los objetos suman porcentaje sobre la base (0.15 = +15%)
    attackSpeed: def.attackSpeed * (1 + bonus.attackSpeed),
    attackRange: def.attackRange,
    armor: def.armor + def.armorPerLevel * growth + bonus.armor,
    abilityHaste: def.abilityHaste + bonus.abilityHaste,
    moveSpeed: def.speed + bonus.moveSpeed,
    omnivamp: def.omnivamp + bonus.omnivamp,
    lethality: def.lethality + bonus.lethality,
    armorPen: Math.min(1, def.armorPen + bonus.armorPen),
    tenacity: Math.min(1, def.tenacity + bonus.tenacity),
  };
}

/** AD actual / AD base: el autoataque escala 100% con esto. */
export function attackDamageRatio(heroId: HeroId, level: number, items: readonly string[] = []): number {
  return heroStats(heroId, level, items).attackDamage / getHeroDef(heroId).attacks[0].damage;
}

/**
 * Daño seguro de un ataque básico (el golpe más flojo del combo, ya con AD e
 * ítems y redondeado como `damageUnit`). Los minions no tienen armadura: un
 * minion con esta vida o menos muere con el siguiente básico (aviso de last hit).
 */
export function basicAttackDamage(heroId: HeroId, level: number, items: readonly string[] = []): number {
  const weakest = Math.min(...getHeroDef(heroId).attacks.map((attack) => attack.damage));
  return Math.round(weakest * attackDamageRatio(heroId, level, items));
}

/** Fracción del aumento de AD que se suma al daño de las habilidades. */
export const ABILITY_AD_SCALING = 0.6;

/**
 * Multiplicador de daño de una habilidad: rango × escalado con el AD.
 * Es proporcional (no un bonus fijo por golpe) para que las habilidades de
 * varios golpes no se disparen.
 */
export function abilityPower(
  heroId: HeroId,
  level: number,
  slot: AbilitySlot,
  rank: number,
  items: readonly string[] = [],
): number {
  const adScale = 1 + ABILITY_AD_SCALING * (attackDamageRatio(heroId, level, items) - 1);
  return abilityDamageScale(slot, rank) * adScale;
}

/** Multiplicador de daño recibido por armadura (fórmula de LoL). */
export function armorMultiplier(armor: number): number {
  return 100 / (100 + Math.max(0, armor));
}

/** Armadura efectiva tras aplicar la penetración (% y plana) del atacante. */
export function effectiveArmor(armor: number, armorPen: number, lethality: number): number {
  return Math.max(0, armor * (1 - armorPen) - lethality);
}

/** Multiplicador de cooldown por aceleración de habilidad (0 = base). */
export function abilityHasteMultiplier(abilityHaste: number): number {
  return 100 / (100 + Math.max(0, abilityHaste));
}

/** Duración de control (hitstun) tras aplicar la tenacidad del objetivo (0..1). */
export function controlDuration(ticks: number, tenacity: number): number {
  return Math.max(0, Math.round(ticks * (1 - Math.max(0, Math.min(1, tenacity)))));
}

/** Orden en que los bots suben habilidades: R cuando se pueda, luego Q > W > E. */
export const BOT_RANK_PRIORITY: AbilitySlot[] = ['r', 'q', 'w', 'e'];
